import { DigitalAsset } from '../../../../modules/domain/digitalAsset/types'
import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Button,
  ConfigProvider,
  Descriptions,
  Divider,
  Empty,
  Flex,
  Input,
  List,
  Typography,
  DescriptionsProps,
  GetProps,
} from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextFilled,
  RollbackOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { formatIsoDate, normalizeUrl } from '../../../../modules/utils/helpers'
import { AssetType } from '../../../../types/entities'
import DADropdownActions from '../../../components/DigitalAssets/DADropdownActions'
import CSS from 'csstype'
import DAFileSizeSpan from '../../../components/DigitalAssets/DAFileSizeSpan'
import { useTranslation } from 'react-i18next'
import { useDigitalAssetActions } from '../hooks/useDigitalAssetActions'

const { Text, Link } = Typography
type SearchProps = GetProps<typeof Input.Search>

type DigitalAssetDetailProps = {
  digitalAsset?: DigitalAsset
  canEditOrDelete: boolean
}

const documentIconContainerStyles: CSS.Properties = {
  paddingTop: '1em',
  paddingBottom: '0.5em',
  width: '100%',
  borderRadius: '20px',
  backgroundColor: '#FAFAFA',
}

const documentButtonContainerStyles: CSS.Properties = {
  display: 'flex',
  justifyContent: 'right',
  width: '100%',
  marginTop: '-16px',
  marginBottom: '-16px',
}

const descriptionStyles: CSS.Properties = {
  color: '#0C0C0C',
  fontWeight: '500',
}

const fileTextIconStyles: CSS.Properties = {
  fontSize: '4em',
  color: '#A09898',
}

const documentNameStyles: CSS.Properties = {
  color: '#8B8282',
  fontWeight: '500',
}

const linkStyles: CSS.Properties = {
  textDecoration: 'underline',
}

const containerStyles: CSS.Properties = {
  paddingRight: '1.5em',
  paddingLeft: '1.5em',
}

const textStyles: CSS.Properties = {
  fontWeight: '500',
}

const sharedCompanyListStyles: CSS.Properties = {
  backgroundColor: '#f5faff',
  marginBottom: '1em',
  borderRadius: '10px',
  borderBottom: 'none',
}

const sharedCompanyContainerStyles: CSS.Properties = {
  paddingTop: '1.5em',
}

const digitalAssetDetailContainerStyles: CSS.Properties = {
  paddingBottom: '0.5em',
}

const digitalAssetDetailStyles: CSS.Properties = {
  paddingBottom: '1em',
}

const digitalAssetMetadataStyles: CSS.Properties = {
  display: 'flex',
  justifyContent: 'flex-end',
}

const rightAlignedContentStyle: CSS.Properties = {
  textAlign: 'right',
  wordBreak: 'break-word',
  maxWidth: '100%',
}

const sharedCompanyProvinceStyles: CSS.Properties = {
  color: '#49454F',
}

const sharedCompanyNameStyles: CSS.Properties = {
  color: '#1D1B20',
}

const DigitalAssetDetail: React.FC<DigitalAssetDetailProps> = ({ digitalAsset, canEditOrDelete }) => {
  const { t } = useTranslation('digitalAsset')
  const [sharedCompanyList, setSharedCompanyList] = useState(digitalAsset?.linked_companies || [])

  // Use the consolidated action hook
  const {
    handleEdit,
    handleDownload,
    handleDelete,
    handleRestore,
    handlePermanentDelete,
    canDownload,
    isDeleted,
    contextHolder,
  } = useDigitalAssetActions({ digitalAsset: digitalAsset!, canEditOrDelete })

  useEffect(() => {
    if (digitalAsset?.linked_companies) {
      setSharedCompanyList(digitalAsset.linked_companies)
    }
  }, [digitalAsset?.linked_companies])

  if (!digitalAsset) {
    return (
      <Flex align="center" justify="center" flex={1}>
        <Empty description={t('details.empty')} />
      </Flex>
    )
  }

  const digitalAssetDetails: DescriptionsProps['items'] = [
    digitalAsset?.link_url
      ? {
          key: '1',
          label: t('details.linkUrl'),
          children: (
            <Link
              href={normalizeUrl(digitalAsset?.link_url || '')}
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyles}
            >
              {digitalAsset?.link_url}
            </Link>
          ),
        }
      : {
          key: '1',
          children: (
            <div style={documentIconContainerStyles}>
              <Flex gap="small" vertical justify="space-around" align="center">
                <FileTextFilled style={fileTextIconStyles} />
                <Text style={documentNameStyles}>{digitalAsset?.document_name}</Text>
              </Flex>
            </div>
          ),
        },
    {
      key: '2',
      children: (
        <div style={documentButtonContainerStyles}>
          <Flex gap="small" justify="center">
            {isDeleted ? (
              // Deleted assets: Restore and Permanent Delete
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<RollbackOutlined />}
                  onClick={handleRestore}
                  title={t('button.restore')}
                  className="download-button-hover"
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handlePermanentDelete}
                  title={t('button.delete_trash')}
                  className="delete-button-hover"
                />
              </>
            ) : (
              // Active assets: Edit, Download, and Delete
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                  disabled={!canEditOrDelete}
                  title={t('button.edit')}
                  className={canEditOrDelete ? 'download-button-hover' : ''}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  disabled={!canDownload}
                  title={t('button.download')}
                  className={canDownload ? 'download-button-hover' : ''}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  disabled={!canEditOrDelete}
                  title={t('button.delete')}
                  className={canEditOrDelete ? 'delete-button-hover' : ''}
                />
              </>
            )}
          </Flex>
        </div>
      ),
    },
    {
      key: '3',
      label: t('details.description'),
      children: <span style={descriptionStyles}>{digitalAsset?.description}</span>,
    },
  ]

  const digitalAssetMetadata: DescriptionsProps['items'] = [
    {
      key: '1',
      label: t('details.creationDate'),
      children: <span style={rightAlignedContentStyle}>{formatIsoDate(digitalAsset.created_at)}</span>,
    },
    {
      key: '2',
      label: t('details.company'),
      children: <span style={rightAlignedContentStyle}>{digitalAsset?.company_name || '--'}</span>,
    },
    {
      key: '3',
      label: t('details.createdBy'),
      children: <span style={rightAlignedContentStyle}>{digitalAsset?.user_name || '--'}</span>,
    },
    {
      key: '4',
      label: t('details.fileType'),
      children: (
        <span style={rightAlignedContentStyle}>
          {digitalAsset?.asset_type == AssetType.Document ? digitalAsset.document_type : t('details.defaultLink')}
        </span>
      ),
    },
    {
      key: '5',
      label: t('details.fileSize'),
      children: (
        <span style={rightAlignedContentStyle}>
          <DAFileSizeSpan fileSize={digitalAsset?.file_size} />
        </span>
      ),
    },
  ]

  const filterCompanies: SearchProps['onSearch'] = (value, _e, _info) => {
    setSharedCompanyList(
      () =>
        digitalAsset?.linked_companies?.filter((company) => {
          return company.name.toLowerCase().includes(value.toLowerCase())
        }) || [],
    )
  }

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          components: {
            Descriptions: {
              labelColor: '#635959',
              itemPaddingBottom: 2,
            },
          },
        }}
      >
        <Flex vertical flex={1}>
          {/*header*/}
          <Flex justify="space-between" align="center" style={containerStyles}>
            <Text style={textStyles}>
              {digitalAsset.link_url ? t('details.linkHeader') : t('details.documentHeader')}
            </Text>
            {digitalAsset && <DADropdownActions digitalAsset={digitalAsset} canEditOrDelete={canEditOrDelete} />}
          </Flex>
          <Divider size="small" />

          <div style={containerStyles}>
            {/*file detail*/}
            <Descriptions
              items={digitalAssetDetails}
              column={1}
              layout="vertical"
              styles={{
                content: {
                  ...digitalAssetDetailStyles,
                },
                root: {
                  ...digitalAssetDetailContainerStyles,
                },
              }}
            />

            {/*file metadata*/}
            <Text style={textStyles}>Information</Text>
            <Divider size="small" />
            <Descriptions
              items={digitalAssetMetadata}
              column={1}
              size="middle"
              styles={{
                content: {
                  ...digitalAssetMetadataStyles,
                  ...textStyles,
                },
              }}
            />

            {/*shared companies*/}
            <Flex vertical style={sharedCompanyContainerStyles}>
              <Text style={textStyles}>{t('details.shareWith')}</Text>
              <Divider size="small" />
              <Input.Search placeholder={t('details.searchRetailersBar')} onSearch={filterCompanies}></Input.Search>
              <div style={sharedCompanyContainerStyles}>
                <Text>
                  {sharedCompanyList.length} {t('details.searchRetailersBar')}
                </Text>
                <List
                  dataSource={sharedCompanyList}
                  renderItem={(item) => (
                    <List.Item style={sharedCompanyListStyles}>
                      <Flex gap="middle" align="center" style={containerStyles}>
                        <Avatar icon={<UserOutlined />} />
                        <Flex vertical>
                          <Text
                            style={{
                              ...sharedCompanyProvinceStyles,
                              ...textStyles,
                            }}
                          >
                            {item.province}
                          </Text>
                          <Text
                            style={{
                              ...sharedCompanyNameStyles,
                              ...textStyles,
                            }}
                          >
                            {item.name}
                          </Text>
                        </Flex>
                      </Flex>
                    </List.Item>
                  )}
                />
              </div>
            </Flex>
          </div>
        </Flex>
      </ConfigProvider>
    </>
  )
}

export default DigitalAssetDetail
